/** Operational components: state in the shared vocabulary, timelines, code, scanning, confirmation. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DATA_STATE,
  DEVICE_LIFECYCLE,
  MANUFACTURING_STATE,
  ORDER_STATE,
  SERVICE_STATE,
  SEVERITY,
  formatAgoShort,
  formatDateTime,
  type DataState,
  type DeviceLifecycle,
  type LifecycleEvent,
  type Link as DeviceLink,
  type ManufacturingState,
  type OrderState,
  type ServiceState,
  type Severity,
  type Tone,
} from '@p1plug/platform';
import { Button, StatusIndicator, StatusMark } from './ds';

// ---- the vocabulary, rendered ---------------------------------------------------

/** Icon and word in a table cell or a line of text (never a coloured pill). */
export function State({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className={`p1-status p1-status--${tone} p1x-state`}>
      <StatusMark tone={tone} />
      <span>{label}</span>
    </span>
  );
}

export const LifecycleState = ({ value }: { value: DeviceLifecycle }) => (
  <State tone={DEVICE_LIFECYCLE[value].tone} label={DEVICE_LIFECYCLE[value].label} />
);
export const DataStateLabel = ({ value }: { value: DataState }) => (
  <State tone={DATA_STATE[value].tone} label={DATA_STATE[value].label} />
);
export const OrderStateLabel = ({ value }: { value: OrderState }) => (
  <State tone={ORDER_STATE[value].tone} label={ORDER_STATE[value].label} />
);
export const ManufacturingStateLabel = ({ value }: { value: ManufacturingState }) => (
  <State tone={MANUFACTURING_STATE[value].tone} label={MANUFACTURING_STATE[value].label} />
);
export const ServiceStateLabel = ({ value }: { value: ServiceState }) => (
  <State tone={SERVICE_STATE[value].tone} label={SERVICE_STATE[value].label} />
);
export const SeverityLabel = ({ value }: { value: Severity }) => (
  <State tone={SEVERITY[value].tone} label={SEVERITY[value].label} />
);

/** Signal quality as support reads it: a word first, the figures after. Thresholds for LTE-M RSRP. */
export function signalQuality(rsrpDbm: number): { label: string; tone: Tone; bars: 1 | 2 | 3 | 4 } {
  if (rsrpDbm >= -90) return { label: 'Good', tone: 'success', bars: 4 };
  if (rsrpDbm >= -100) return { label: 'Fair', tone: 'success', bars: 3 };
  if (rsrpDbm >= -110) return { label: 'Weak', tone: 'warning', bars: 2 };
  return { label: 'Very weak', tone: 'critical', bars: 1 };
}

export function Signal({ link, detail = false }: { link: DeviceLink; detail?: boolean }) {
  if (link.kind === 'lan')
    return <span className="p1x-signal">Local network{detail && link.hostname ? ` · ${link.hostname}` : ''}</span>;
  if (link.kind === 'unknown') return <span className="p1x-signal p1x-muted">–</span>;
  const q = signalQuality(link.rsrpDbm);
  return (
    <span className="p1x-signal" aria-label={`Signal ${q.label}, RSRP ${link.rsrpDbm} dBm`}>
      <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true" focusable="false">
        {[0, 1, 2, 3].map((i) => (
          <rect
            key={i}
            x={i * 4.5}
            y={10 - i * 3}
            width="3"
            height={4 + i * 3}
            rx="1"
            className={i < q.bars ? 'p1x-signal-on' : 'p1x-signal-off'}
          />
        ))}
      </svg>
      <span>{q.label}</span>
      {detail ? (
        <span className="p1x-muted p1-num">
          {' '}
          · RSRP {link.rsrpDbm} dBm · RSRQ {link.rsrqDb} dB
        </span>
      ) : null}
    </span>
  );
}

// ---- timeline -------------------------------------------------------------------

/** A chronological event stream, newest first: what happened, when, by whom. */
export function Timeline({ events, now, empty }: { events: LifecycleEvent[]; now: Date; empty: string }) {
  if (events.length === 0) return <p className="p1x-muted">{empty}</p>;
  return (
    <ol className="p1x-timeline" role="list">
      {events.map((e, i) => (
        <li key={`${e.at}-${i}`} className={e.tone ? `p1x-timeline-item p1-status--${e.tone}` : 'p1x-timeline-item'}>
          <span className="p1x-timeline-dot" aria-hidden="true">
            {e.tone && e.tone !== 'unknown' ? <StatusMark tone={e.tone} size={14} /> : null}
          </span>
          <div className="p1x-timeline-body">
            <p className="p1x-timeline-title">{e.title}</p>
            {e.detail ? <p className="p1x-timeline-detail">{e.detail}</p> : null}
            <p className="p1x-timeline-meta p1-num">
              <time dateTime={e.at} title={e.at}>
                {formatDateTime(e.at, 'en')}
              </time>{' '}
              · {formatAgoShort(e.at, now)} ago{e.actor ? ` · ${e.actor}` : ''}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---- code -----------------------------------------------------------------------

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <button type="button" className="p1x-copy" onClick={async () => setCopied(await copyText(text))}>
      <span role="status">{copied ? 'Copied' : label}</span>
    </button>
  );
}

export interface CodeSample {
  /** Tab label: cURL, TypeScript, Python, C#. */
  language: string;
  code: string;
}

/** A code example, optionally in several languages. The chosen language is remembered across blocks. */
export function CodeBlock({
  samples,
  title,
  language,
  onLanguage,
}: {
  samples: CodeSample[];
  title?: string;
  language?: string;
  onLanguage?: (language: string) => void;
}) {
  const [own, setOwn] = useState(samples[0]!.language);
  const wanted = language ?? own;
  const active = samples.find((s) => s.language === wanted) ?? samples[0]!;
  const choose = (l: string) => (onLanguage ? onLanguage(l) : setOwn(l));
  return (
    <div className="p1x-code">
      <div className="p1x-code-head">
        {samples.length > 1 ? (
          <div className="p1x-code-tabs" role="group" aria-label="Language">
            {samples.map((s) => (
              <button
                key={s.language}
                type="button"
                className="p1x-code-tab"
                aria-pressed={s.language === active.language}
                onClick={() => choose(s.language)}
              >
                {s.language}
              </button>
            ))}
          </div>
        ) : (
          <span className="p1x-code-title">{title ?? active.language}</span>
        )}
        <CopyButton text={active.code} />
      </div>
      <pre tabIndex={0}>
        <code>{active.code}</code>
      </pre>
    </div>
  );
}

/** An API operation: method and path, as developers scan for them. */
export function ApiPath({ method, path }: { method: string; path: string }) {
  return (
    <span className="p1x-api">
      <span className={`p1x-api-method p1x-api-method--${method.toLowerCase()}`}>{method}</span>
      <code className="p1x-api-path">{path}</code>
    </span>
  );
}

// ---- workstation ----------------------------------------------------------------

/**
 * The one field of a scan screen. It keeps focus, so a barcode scanner (a
 * keyboard that types and presses Enter) or a typist can work without a mouse.
 */
export function ScanInput({
  label,
  hint,
  example,
  onScan,
  error,
  busy,
}: {
  label: string;
  hint?: string;
  example?: string;
  onScan: (value: string) => void;
  error?: string;
  busy?: boolean;
}) {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!busy) ref.current?.focus();
  }, [busy]);
  return (
    <form
      className={`p1x-scan${error ? ' has-error' : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        const v = value.trim();
        if (!v || busy) return;
        setValue('');
        onScan(v);
      }}
    >
      <label className="p1-label" htmlFor="p1x-scan">
        {label}
      </label>
      {hint ? (
        <span className="p1-hint" id="p1x-scan-hint">
          {hint}
        </span>
      ) : null}
      <div className="p1x-scan-row">
        <input
          ref={ref}
          id="p1x-scan"
          className="p1-input p1x-scan-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={example ? `For example ${example}` : undefined}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-describedby={hint ? 'p1x-scan-hint' : undefined}
          aria-invalid={error ? 'true' : undefined}
          disabled={busy}
          autoFocus
        />
        <Button label="Enter" type="submit" variant="secondary" loading={busy} />
      </div>
      {error ? (
        <span className="p1-field-error" role="alert">
          <StatusMark tone="critical" />
          <span>{error}</span>
        </span>
      ) : null}
    </form>
  );
}

/** The large station state: READY, TESTING, PASS, FAIL. Readable from across a workbench. */
export function BigState({ tone, word, detail }: { tone: Tone; word: string; detail?: ReactNode }) {
  return (
    <div className={`p1x-bigstate p1-status--${tone}`} role="status">
      <span className="p1x-bigstate-word">
        <StatusMark tone={tone} size={40} />
        {word}
      </span>
      {detail ? <span className="p1x-bigstate-detail">{detail}</span> : null}
    </div>
  );
}

// ---- controlled actions ---------------------------------------------------------

/**
 * Confirmation for an action with consequences. The operator types the
 * object's identifier; the button names the consequence and its scope.
 */
export function ConfirmAction({
  title,
  consequence,
  confirmWord,
  actionLabel,
  onConfirm,
  onCancel,
  busy,
  error,
}: {
  title: string;
  consequence: ReactNode;
  confirmWord: string;
  actionLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  error?: string;
}) {
  const [typed, setTyped] = useState('');
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);
  const matches = typed.trim().toUpperCase() === confirmWord.toUpperCase();
  return (
    <dialog ref={ref} className="p1x-dialog" aria-labelledby="p1x-dialog-title" onCancel={onCancel} onClose={onCancel}>
      <h2 id="p1x-dialog-title" className="p1x-dialog-title">
        {title}
      </h2>
      <div className="p1x-dialog-text">{consequence}</div>
      <div className="p1-field">
        <label className="p1-label" htmlFor="p1x-confirm">
          Type {confirmWord} to confirm
        </label>
        <input
          id="p1x-confirm"
          className="p1-input p1-data"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {error ? <StatusIndicator tone="critical" label={error} /> : null}
      <div className="p1x-dialog-actions">
        <Button label="Cancel" variant="secondary" onClick={onCancel} />
        <Button
          label={actionLabel}
          variant="destructive"
          disabled={!matches}
          reason={matches ? undefined : `Enabled once ${confirmWord} is typed.`}
          loading={busy}
          onClick={onConfirm}
        />
      </div>
    </dialog>
  );
}
